import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError, SeriesReportQuery, defaultReportYears, type TShowSeries } from '@schemas'
import { useSeriesContext } from '@/layouts/SeriesLayout'
import {
  reportActorKey,
  useCurrentUser,
  useSeriesTrendIdentities,
  useSeriesEditions,
  useSeriesTrendReport,
} from '@/api/hooks'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SeriesDashboard } from '@/features/series-report/Dashboard'
import { EditionPublication } from '@/features/series-publication/PublicationControls'
import { PageHeading } from '@/components/page-heading'

function SeriesReport({
  start,
  end,
  seriesId,
  scope,
}: {
  start: number
  end: number
  seriesId: string
  scope: string
}) {
  const [params, setParams] = useSearchParams()
  const fromYear = params.get('fromYear') ?? String(start)
  const toYear = params.get('toYear') ?? String(end)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [classSearch, setClassSearch] = useState('')
  const [denied, setDenied] = useState(false)
  const identities = useSeriesTrendIdentities(seriesId, !denied)
  const available = [
    ...new Map(
      (identities.data?.pages.flatMap(page => page.identities) ?? []).map(i => [i.id, i])
    ).values(),
  ]
  const automatic = !params.has('identityIds')
  const selected = automatic
    ? available
        .slice(0, 100)
        .map(i => i.id)
        .sort()
    : [...new Set((params.get('identityIds') ?? '').split(',').filter(Boolean))].sort()
  const selectionLoading =
    automatic &&
    !identities.isError &&
    (identities.isPending || (identities.hasNextPage && available.length < 100))
  const exceedsLimit = available.length > 100 || Boolean(identities.hasNextPage)
  useEffect(() => {
    if (
      automatic &&
      available.length < 100 &&
      identities.hasNextPage &&
      !identities.isFetching &&
      !identities.isError
    )
      void identities.fetchNextPage()
  }, [
    automatic,
    available.length,
    identities.hasNextPage,
    identities.isFetching,
    identities.isError,
    identities.fetchNextPage,
  ])
  const parsed = SeriesReportQuery.safeParse({
    fromYear,
    toYear,
    ...(selected.length ? { identityIds: selected.join(',') } : {}),
  })
  const qc = useQueryClient()
  const report = useSeriesTrendReport(
    seriesId,
    parsed.success ? parsed.data : { fromYear: end, toYear: end },
    parsed.success && !denied && !selectionLoading
  )
  useEffect(
    () => () => {
      const filters = {
        predicate: (query: { queryKey: readonly unknown[] }) =>
          query.queryKey[0] === 'show-series' &&
          ['report', 'report-identities'].includes(String(query.queryKey[1])) &&
          query.queryKey[2] === seriesId &&
          query.queryKey[3] === scope,
      }
      void qc.cancelQueries(filters)
      qc.removeQueries(filters)
    },
    [qc, scope, seriesId]
  )
  const error = report.error ?? identities.error
  useEffect(() => {
    if (
      error instanceof ApiError &&
      [401, 403, 404].includes(error.status) &&
      error.payload?.code !== 'SERIES_IDENTITY_NOT_FOUND'
    ) {
      setDenied(true)
      void qc.cancelQueries({ queryKey: ['show-series'] })
      qc.removeQueries({ queryKey: ['show-series'] })
    }
  }, [error, qc])
  function change(key: string, value: string) {
    const next = new URLSearchParams(params)
    next.set('fromYear', fromYear)
    next.set('toYear', toYear)
    if (value || key === 'fromYear' || key === 'toYear' || key === 'identityIds')
      next.set(key, value)
    else next.delete(key)
    setParams(next, { replace: true })
  }
  if (denied)
    return (
      <p role="alert">
        Series data is no longer available with your current access or membership. Private cached
        results were cleared.
      </p>
    )
  return (
    <div className="space-y-6 min-w-0">
      <PageHeading level={2} runLevel={3}>
        Series trends
      </PageHeading>
      <details className="text-sm text-muted-foreground space-y-2">
        <summary className="cursor-pointer underline">About this report</summary>
        <p>
          Live staff report. Includes persisted draft entries and excludes not-entered entries. Each
          year is an independent population of entrants with counted entries; annual entrant counts
          are not unique people across years.
        </p>
        <p>
          Overall totals include classes still needing review and classes excluded from year-to-year
          comparison, so confirmed class trends may not sum to the total. Unfinished editions are
          provisional. Disabled club tracking and years without readable data are unavailable, not
          zero.
        </p>
      </details>
      <div className="flex flex-wrap gap-3 items-end">
        <label>
          From year
          <Input
            aria-label="From year"
            type="number"
            min={1000}
            max={9999}
            value={fromYear}
            onChange={e => change('fromYear', e.target.value)}
          />
        </label>
        <label>
          To year
          <Input
            aria-label="To year"
            type="number"
            min={1000}
            max={9999}
            value={toYear}
            onChange={e => change('toYear', e.target.value)}
          />
        </label>
        <Button
          variant="outline"
          onClick={() => setPickerOpen(!pickerOpen)}
          aria-expanded={pickerOpen}
        >
          Choose classes ({selected.length} selected)
        </Button>
        <Button
          variant="outline"
          disabled={
            identities.isPending ||
            identities.isError ||
            (!available.length && !identities.hasNextPage)
          }
          onClick={() => {
            const next = new URLSearchParams(params)
            next.delete('identityIds')
            setParams(next, { replace: true })
          }}
        >
          {exceedsLimit
            ? available.length >= 100
              ? 'Select first 100'
              : 'Select all (up to 100)'
            : 'Select all'}
        </Button>
        <Button
          variant="outline"
          disabled={!selected.length && !selectionLoading}
          onClick={() => change('identityIds', '')}
        >
          Clear all
        </Button>
      </div>
      <p role="status" className="text-sm text-muted-foreground">
        {selectionLoading
          ? 'Loading matched classes…'
          : !identities.hasNextPage &&
              available.length > 0 &&
              selected.length === available.length &&
              available.every(i => selected.includes(i.id))
            ? `All ${available.length} matched ${available.length === 1 ? 'class' : 'classes'} selected.`
            : `${selected.length} ${selected.length === 1 ? 'class' : 'classes'} selected.`}
        {exceedsLimit &&
          ' Up to 100 classes can be shown at once. Choose individual classes to change the selection.'}
      </p>
      {identities.isError && (
        <div role="alert">
          <p>Unable to load the class list.</p>
          <Button variant="outline" onClick={() => void identities.refetch()}>
            Retry class list
          </Button>
        </div>
      )}
      {identities.isSuccess && !available.length && !identities.hasNextPage && (
        <p>
          No classes have been matched across years yet.{' '}
          <Link className="underline" to={`/show-series/${seriesId}/alignment`}>
            Match classes across years
          </Link>{' '}
          to see their history here.
        </p>
      )}
      {!parsed.success && (
        <p role="alert">
          Choose valid years spanning at most 50 calendar years and at most 100 distinct class
          identities.
        </p>
      )}
      {pickerOpen && (
        <fieldset className="border rounded p-3 space-y-2">
          <legend>Classes to show</legend>
          <p>
            Choose classes to include in the charts. Select all and Clear all apply to the entire
            class list, including classes hidden by search.
          </p>
          <label className="block">
            Find matched classes
            <Input
              value={classSearch}
              onChange={e => setClassSearch(e.target.value)}
              placeholder="Class name"
            />
          </label>
          {identities.isPending && <p role="status">Loading classes…</p>}
          {identities.isError ? (
            <>
              <p role="alert">Unable to load classes.</p>
              <Button onClick={() => void identities.refetch()}>Retry classes</Button>
            </>
          ) : (
            available
              .filter(identity =>
                identity.label.toLocaleLowerCase().includes(classSearch.trim().toLocaleLowerCase())
              )
              .map(identity => (
                <label key={identity.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selected.includes(identity.id)}
                    disabled={!selected.includes(identity.id) && selected.length >= 100}
                    onChange={() =>
                      change(
                        'identityIds',
                        (selected.includes(identity.id)
                          ? selected.filter(id => id !== identity.id)
                          : [...selected, identity.id]
                        )
                          .sort()
                          .join(',')
                      )
                    }
                  />
                  {identity.label}
                  {identity.retired && ' (retired)'}
                </label>
              ))
          )}
          {identities.hasNextPage && (
            <Button
              disabled={identities.isFetchingNextPage}
              onClick={() => void identities.fetchNextPage()}
            >
              More matched classes
            </Button>
          )}
          {available.length > 0 &&
            !available.some(i =>
              i.label.toLocaleLowerCase().includes(classSearch.trim().toLocaleLowerCase())
            ) && <p>No classes match this search.</p>}
        </fieldset>
      )}
      {parsed.success &&
        (report.isError ? (
          <div role="alert">
            <p>Unable to load the report. {report.error.message}</p>
            <Button onClick={() => void report.refetch()}>Retry report</Button>
          </div>
        ) : report.isPending ? (
          <p role="status">Loading live series report…</p>
        ) : (
          report.data && (
            <>
              <h2 className="text-xl font-semibold">{report.data.series.name}</h2>
              <SeriesDashboard report={report.data} seriesId={seriesId} />
            </>
          )
        ))}
      {!denied && !report.isError && report.data && (
        <section className="space-y-4" aria-label="Edition publications">
          <h2 className="text-xl font-semibold">Edition publications</h2>
          {report.data!.years.flatMap(({ year, edition }) =>
            edition
              ? [
                  <EditionPublication
                    key={`${edition.showId}:${scope}`}
                    showId={edition.showId}
                    year={year}
                    name={edition.name}
                  />,
                ]
              : []
          )}
        </section>
      )}
    </div>
  )
}

function SeriesReportEditions({ series, scope }: { series: TShowSeries; scope: string }) {
  const editions = useSeriesEditions(series.id)
  useEffect(() => {
    if (editions.hasNextPage && !editions.isFetching && !editions.isError)
      void editions.fetchNextPage()
  }, [editions.hasNextPage, editions.isFetching, editions.isError, editions.fetchNextPage])
  if (editions.isError)
    return (
      <div role="alert">
        <p>Unable to load series editions.</p>
        <Button onClick={() => void editions.refetch()}>Retry editions</Button>
      </div>
    )
  if (editions.isPending || editions.hasNextPage)
    return <p role="status">Loading series editions…</p>
  const years = editions.data.pages.flatMap(page => page.editions.map(edition => edition.year))
  if (!years.length)
    return (
      <p>No readable editions are in this series yet. Add a show in Settings to view reports.</p>
    )
  return (
    <SeriesReport
      start={defaultReportYears(years).fromYear}
      end={defaultReportYears(years).toYear}
      seriesId={series.id}
      scope={scope}
    />
  )
}
export default function SeriesTrendReportPage() {
  const series = useSeriesContext()
  const user = useCurrentUser()
  if (!user.data) return <p role="status">Checking series access…</p>
  const scope = JSON.stringify(reportActorKey(user.data))
  return <SeriesReportEditions key={`${series.id}:${scope}`} series={series} scope={scope} />
}
